"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/tailwind";

// Hook to check archival mode (lazy import to avoid circular deps)
let useArchivalModeHook: (() => { isArchivalMode: boolean }) | null = null;
try {
  const archivalContext = require("@/contexts/ArchivalModeContext");
  useArchivalModeHook = archivalContext.useArchivalMode;
} catch {
  // Context not available
}

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background cursor-pointer transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 backdrop-blur-sm active:scale-95",
  {
    variants: {
      variant: {
        default:
          "bg-linear-to-b from-primary to-blue-600 text-primary-foreground hover:from-primary/90 hover:to-blue-600/90 shadow-[0_0_20px_rgba(77,163,255,0.3)] hover:shadow-[0_0_30px_rgba(77,163,255,0.5)] border border-blue-400/20",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm",
        outline:
          "border border-input bg-background/50 hover:bg-accent hover:text-accent-foreground backdrop-blur-md hover:border-primary/50",
        secondary:
          "bg-secondary/50 text-secondary-foreground hover:bg-secondary/80 border border-white/5",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabled, children, title, ...props }, ref) => {
    // Check archival mode
    let isArchivalMode = false;
    try {
      if (useArchivalModeHook) {
        const archival = useArchivalModeHook();
        isArchivalMode = archival.isArchivalMode;
      }
    } catch {
      // Context not available
    }

    // Disable ALL buttons in archival mode - EXCEPT those with data-allow-archival-edit
    const allowInArchival = (props as any)["data-allow-archival-edit"] === true;
    const effectiveDisabled = disabled || (isArchivalMode && !allowInArchival);

    // Completely prevent onClick from firing when in archival mode
    // Create a blocking handler that prevents ALL event propagation
    // UNLESS the button has data-allow-archival-edit
    const handleClick = (isArchivalMode && !allowInArchival)
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
        // Block everything - prevent default, stop propagation at all levels
        e.preventDefault();
        e.stopPropagation();
        if (e.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
          e.nativeEvent.preventDefault();
        }
        // Remove any onclick handlers
        if (e.currentTarget) {
          try {
            e.currentTarget.onclick = null;
            // Also remove via removeEventListener if possible
            const target = e.currentTarget as any;
            if (target.removeEventListener) {
              // Clone the element's event listeners and remove them
              const newElement = target.cloneNode(true);
              target.parentNode?.replaceChild(newElement, target);
            }
          } catch (err) {
            // Ignore errors
          }
        }
        return false;
      }
      : effectiveDisabled
        ? (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        : props.onClick;

    // Separate onClick from other props to ensure our handler takes precedence
    // Also remove onMouseDown, onMouseUp, onTouchStart, onTouchEnd to prevent any interaction
    const {
      onClick: _onClick,
      onMouseDown: _onMouseDown,
      onMouseUp: _onMouseUp,
      onTouchStart: _onTouchStart,
      onTouchEnd: _onTouchEnd,
      ...restProps
    } = props;

    // In archival mode, block all pointer events and add additional event blockers
    // UNLESS the button has data-allow-archival-edit
    const archivalStyle = (isArchivalMode && !allowInArchival) ? {
      pointerEvents: 'none' as const,
      cursor: 'not-allowed' as const,
      userSelect: 'none' as const,
      WebkitUserSelect: 'none' as const,
    } : {};

    // Block all mouse/touch events in archival mode
    // UNLESS the button has data-allow-archival-edit
    const blockAllEvents = (isArchivalMode && !allowInArchival) ? {
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
      onMouseUp: (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
      onTouchStart: (e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
      onTouchEnd: (e: React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      },
    } : {};

    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={effectiveDisabled}
        title={title}
        onClick={handleClick}
        style={{ ...archivalStyle, ...restProps.style }}
        {...blockAllEvents}
        {...restProps}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
